import { ApiResponseError } from '@/lib/api-fetch'
import { InventoryVariantUnavailableError, getProductMedia } from '@/lib/api/client/inventory'
import { getRecoverySubject, listRecoverySubjects } from '@/lib/api/client/product-recovery'
import type { ProductMedia } from '@/lib/api/schemas/inventory.schema'
import type { RecoverySubjectDetail } from '@/lib/api/schemas/product-recovery.schema'

export const PRODUCT_NOT_FOUND = 'Товар не найден.'

const QUEUE_PAGE = 200

/** A catalogue row that exists but is not a piece, with what it carries. */
export interface LegacyProduct {
  productId: string
  media: ProductMedia
  subject: RecoverySubjectDetail | null
  queueError: string | null
}

/** The piece read's plain absence, which a legacy catalogue row also answers. */
export function isOutsidePiecePopulation(reason: unknown): boolean {
  return reason instanceof InventoryVariantUnavailableError && reason.reason === 'frozen_identity.not_found'
}

// The queue has no read keyed by product, so the subject is found by paging it.
async function findSubjectId(legacyProductId: string, signal: AbortSignal): Promise<string | null> {
  for (let offset = 0; ; offset += QUEUE_PAGE) {
    const page = await listRecoverySubjects({ status: 'all', limit: QUEUE_PAGE, offset }, signal)
    const hit = page.items.find((item) => item.legacyProductId === legacyProductId)
    if (hit) return hit.id
    if (page.items.length < QUEUE_PAGE || offset + page.items.length >= page.total) return null
  }
}

/**
 * `null` only when the catalogue holds no such product: the product media read
 * answers for every unmerged product, so its 404 is the one real absence.
 */
export async function readLegacyProduct(productId: string, signal: AbortSignal): Promise<LegacyProduct | null> {
  let media: ProductMedia
  try {
    media = await getProductMedia(productId, signal)
  } catch (reason) {
    if (reason instanceof ApiResponseError && reason.status === 404) return null
    throw reason
  }
  try {
    const subjectId = await findSubjectId(productId, signal)
    const subject = subjectId === null ? null : await getRecoverySubject(subjectId, signal)
    return { productId, media, subject, queueError: null }
  } catch (reason) {
    if (signal.aborted) throw reason
    return {
      productId,
      media,
      subject: null,
      queueError: reason instanceof Error ? reason.message : 'Очередь сопоставления недоступна.',
    }
  }
}
